import { useState, useMemo } from "react";
import { useQuery, useQueryClient } from "@tanstack/react-query";
import { supabase } from "@/lib/supabase";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription } from "@/components/ui/dialog";
import { Button } from "@/components/ui/button";
import StampForm from "@/components/stamp-form"; 
import { 
  ChevronLeft, 
  ChevronRight, 
  Trash2, 
  Pencil, 
  Signature,
  ShieldCheck,
} from "lucide-react";
import { useToast } from "@/hooks/use-toast";

const STAMPS_PER_PAGE = 4;

const COUNTRY_TO_ISO3: Record<string, string> = {
  "Afghanistan": "AFG", "Albania": "ALB", "Algeria": "DZA", "Andorra": "AND", "Angola": "AGO", "Antigua and Barbuda": "ATG", "Argentina": "ARG", "Armenia": "ARM", "Australia": "AUS", "Austria": "AUT", "Azerbaijan": "AZE",
  "Bahamas": "BHS", "Bahrain": "BHR", "Bangladesh": "BGD", "Barbados": "BRB", "Belarus": "BLR", "Belgium": "BEL", "Belize": "BLZ", "Benin": "BEN", "Bhutan": "BTN", "Bolivia": "BOL", "Bosnia and Herzegovina": "BIH", "Botswana": "BWA", "Brazil": "BRA", "Brunei": "BRN", "Bulgaria": "BGR", "Burkina Faso": "BFA", "Burundi": "BDI",
  "Cabo Verde": "CPV", "Cambodia": "KHM", "Cameroon": "CMR", "Canada": "CAN", "Central African Republic": "CAF", "Chad": "TCD", "Chile": "CHL", "China": "CHN", "Colombia": "COL", "Comoros": "COM", "Congo": "COG", "Costa Rica": "CRI", "Croatia": "HRV", "Cuba": "CUB", "Cyprus": "CYP", "Czech Republic": "CZE",
  "Denmark": "DNK", "Djibouti": "DJI", "Dominica": "DMA", "Dominican Republic": "DOM", "Ecuador": "ECU", "Egypt": "EGY", "El Salvador": "SLV", "Equatorial Guinea": "GNQ", "Eritrea": "ERI", "Estonia": "EST", "Eswatini": "SWZ", "Ethiopia": "ETH",
  "Fiji": "FJI", "Finland": "FIN", "France": "FRA", "Gabon": "GAB", "Gambia": "GMB", "Georgia": "GEO", "Germany": "DEU", "Ghana": "GHA", "Greece": "GRC", "Grenada": "GRD", "Guatemala": "GTM", "Guinea": "GIN", "Guinea-Bissau": "GNB", "Guyana": "GUY",
  "Haiti": "HTI", "Honduras": "HND", "Hungary": "HUN", "Iceland": "ISL", "India": "IND", "Indonesia": "IDN", "Iran": "IRN", "Iraq": "IRQ", "Ireland": "IRL", "Israel": "ISR", "Italy": "ITA", "Jamaica": "JAM", "Japan": "JPN", "Jordan": "JOR",
  "Kazakhstan": "KAZ", "Kenya": "KEN", "Kiribati": "KIR", "Korea, North": "PRK", "Korea, South": "KOR", "Kosovo": "XKX", "Kuwait": "KWT", "Kyrgyzstan": "KGZ", "Laos": "LAO", "Latvia": "LVA", "Lebanon": "LBN", "Lesotho": "LSO", "Liberia": "LBR", "Libya": "LBY", "Liechtenstein": "LIE", "Lithuania": "LTU", "Luxembourg": "LUX",
  "Madagascar": "MDG", "Malawi": "MWI", "Malaysia": "MYS", "Maldives": "MDV", "Mali": "MLI", "Malta": "MLT", "Marshall Islands": "MHL", "Mauritania": "MRT", "Mauritius": "MUS", "Mexico": "MEX", "Micronesia": "FSM", "Moldova": "MDA", "Monaco": "MCO", "Mongolia": "MNG", "Montenegro": "MNE", "Morocco": "MAR", "Mozambique": "MOZ", "Myanmar": "MMR",
  "Namibia": "NAM", "Nauru": "NRU", "Nepal": "NPL", "Netherlands": "NLD", "New Zealand": "NZL", "Nicaragua": "NIC", "Niger": "NER", "Nigeria": "NGA", "North Macedonia": "MKD", "Norway": "NOR", "Oman": "OMN",
  "Pakistan": "PAK", "Palau": "PLW", "Palestine": "PSE", "Panama": "PAN", "Papua New Guinea": "PNG", "Paraguay": "PRY", "Peru": "PER", "Philippines": "PHL", "Poland": "POL", "Portugal": "PRT", "Qatar": "QAT",
  "Romania": "ROU", "Russia": "RUS", "Rwanda": "RWA", "Saint Kitts and Nevis": "KNA", "Saint Lucia": "LCA", "Saint Vincent": "VCT", "Samoa": "WSM", "San Marino": "SMR", "Sao Tome and Principe": "STP", "Saudi Arabia": "SAU", "Senegal": "SEN", "Serbia": "SRB", "Seychelles": "SYC", "Sierra Leone": "SLE", "Singapore": "SGP", "Slovakia": "SVK", "Slovenia": "SVN", "Solomon Islands": "SLB", "Somalia": "SOM", "South Africa": "ZAF", "South Sudan": "SSD", "Spain": "ESP", "Sri Lanka": "LKA", "Sudan": "SDN", "Suriname": "SUR", "Sweden": "SWE", "Switzerland": "CHE", "Syria": "SYR",
  "Taiwan": "TWN", "Tajikistan": "TJK", "Tanzania": "TZA", "Thailand": "THA", "Timor-Leste": "TLS", "Togo": "TGO", "Tonga": "TON", "Trinidad and Tobago": "TTO", "Tunisia": "TUN", "Turkey": "TUR", "Turkmenistan": "TKM", "Tuvalu": "TUV", "Uganda": "UGA", "Ukraine": "UA", "United Arab Emirates": "ARE", "United Kingdom": "GBR", "United States": "USA", "Uruguay": "URY", "Uzbekistan": "UZB",
  "Vanuatu": "VUT", "Vatican City": "VAT", "Venezuela": "VEN", "Vietnam": "VNM", "Yemen": "YEM", "Zambia": "ZMB", "Zimbabwe": "ZWE"
};

const USA_FLAG_BLUE = "#0A3161";
const USA_FLAG_RED = "#B31942";

export default function Booklet() {
  const [currentPage, setCurrentPage] = useState(0);
  const [selectedStamp, setSelectedStamp] = useState<any>(null);
  const [editingStamp, setEditingStamp] = useState<any>(null);
  const { toast } = useToast();
  const queryClient = useQueryClient();

  const { data: stamps = [], isLoading } = useQuery({
    queryKey: ["stamps"],
    queryFn: async () => {
      const { data, error } = await supabase
        .from("stamps")
        .select("*")
        .order("stamp_date", { ascending: false });
      if (error) throw error;
      return data || [];
    },
  });

  const pages = useMemo(() => {
    const chunks = [];
    for (let i = 0; i < stamps.length; i += STAMPS_PER_PAGE) {
      chunks.push(stamps.slice(i, i + STAMPS_PER_PAGE));
    }
    return chunks;
  }, [stamps]);

  const handleDelete = async (id: string) => {
    if (!confirm("Revoke this entry?")) return;
    try {
      const { error } = await supabase.from("stamps").delete().eq("id", id);
      if (error) throw error;
      queryClient.invalidateQueries({ queryKey: ["stamps"] });
      setSelectedStamp(null);
      toast({ title: "Entry Revoked" });
    } catch (err: any) {
      toast({ title: "Action Failed", description: err.message, variant: "destructive" });
    }
  };

  if (isLoading) return <div className="p-20 text-center font-serif italic opacity-20 text-black">Opening Netport...</div>;

  return (
    <div className="min-h-screen bg-white p-4 md:p-16 flex flex-col items-center">
      <div className="w-full max-w-4xl bg-[#fdfcf0] border border-black/20 shadow-[0_40px_80px_-20px_rgba(0,0,0,0.15)] relative min-h-[650px] md:min-h-[750px] flex flex-col overflow-hidden">
        
        <div className="absolute inset-0 opacity-[0.15] pointer-events-none bg-[url('https://www.transparenttextures.com/patterns/pinstripe-light.png')] mix-blend-multiply" />
        <div className="absolute inset-0 opacity-[0.08] pointer-events-none bg-[url('https://www.transparenttextures.com/patterns/carbon-fibre.png')] mix-blend-multiply" />

        <header className="p-6 md:p-8 border-b border-black/10 flex justify-between items-end relative z-10">
          <div>
            <p className="text-[10px] font-mono uppercase tracking-[0.4em] text-black/40">Netport Stamps</p>
            <h2 className="font-serif text-xl md:text-xl italic text-black/80">Page {currentPage + 1}</h2>
          </div>
          <div className="flex gap-2">
            <Button variant="ghost" disabled={currentPage === 0} onClick={() => setCurrentPage(prev => prev - 1)} className="rounded-none border border-black/10 h-8 w-8 p-0">
              <ChevronLeft className="w-4 h-4" />
            </Button>
            <Button variant="ghost" disabled={currentPage >= pages.length - 1} onClick={() => setCurrentPage(prev => prev + 1)} className="rounded-none border border-black/10 h-8 w-8 p-0">
              <ChevronRight className="w-4 h-4" />
            </Button>
          </div>
        </header>

        <div className="flex-1 p-6 md:p-12 grid grid-cols-2 grid-rows-2 gap-4 md:gap-x-12 md:gap-y-16 relative z-10">
          {pages[currentPage]?.map((stamp: any, idx: number) => {
            const iso3 = COUNTRY_TO_ISO3[stamp.location]?.toUpperCase() || "";
            const rotation = (idx % 2 === 0 ? -1 : 1) * (idx + 1) * 3;

            const cityAbbr = stamp.location.includes(" ") 
                ? stamp.location.split(" ").map((n: string) => n[0]).join("").substring(0, 3)
                : stamp.location.substring(0, 3);
            
            const displayWatermark = iso3 || cityAbbr.toUpperCase();

            return (
              <div key={stamp.id} onClick={() => setSelectedStamp(stamp)} className="flex items-center justify-center cursor-pointer group">
                <div 
                  className="relative aspect-square w-full max-w-[140px] md:max-w-[176px] rounded-full border-4 border-double flex flex-col items-center justify-center p-2 md:p-4 transition-all duration-300 overflow-hidden shrink-0 bg-transparent group-hover:scale-110 group-hover:bg-[#0A3161]/5 group-hover:shadow-[0_0_20px_rgba(10,49,97,0.1)]"
                  style={{ 
                    transform: `rotate(${rotation}deg)`,
                    borderColor: `${USA_FLAG_BLUE}` // Bold Blue border
                  }}
                >
                  <div className="absolute inset-0 opacity-[0.15] pointer-events-none bg-[url('https://www.transparenttextures.com/patterns/stardust.png')] mix-blend-multiply" />
                  
                  <span className="absolute text-[44px] md:text-[54px] font-black opacity-[0.08] pointer-events-none select-none mix-blend-multiply tracking-tighter" style={{ color: USA_FLAG_BLUE }}>
                    {displayWatermark}
                  </span>

                  <div 
                    className="text-center space-y-0.5 md:space-y-1 z-10 flex flex-col items-center mix-blend-multiply" 
                    style={{ transform: `rotate(${-rotation}deg)` }}
                  >
                    <div className="flex flex-col items-center gap-0">
                      <div className="flex items-center gap-1 justify-center max-w-[90px] md:max-w-[130px]">
                        <p className="font-serif font-black text-[10px] md:text-[12px] uppercase tracking-tighter leading-tight truncate" style={{ color: USA_FLAG_BLUE }}>
                          {stamp.location}
                        </p>
                        {stamp.is_verified && <ShieldCheck className="w-2.5 h-2.5 opacity-60" style={{ color: USA_FLAG_BLUE }} />}
                      </div>
                    </div>
                    
                    <p className="font-mono text-[7px] md:text-[8px] font-bold tracking-widest pt-1" style={{ color: USA_FLAG_RED }}>
                      {stamp.stamp_date ? new Date(stamp.stamp_date).toLocaleDateString(undefined, { month: 'short', day: 'numeric', year: 'numeric' }).toUpperCase() : "PENDING"}
                    </p>
                    
                    {stamp.port_of_entry && (
                      <p className="font-mono text-[8px] md:text-[9px] font-black uppercase tracking-[0.25em] leading-none opacity-100 pt-0.5" style={{ color: USA_FLAG_BLUE }}>
                        {stamp.port_of_entry}
                      </p>
                    )}

                    <div className="pt-1 opacity-[0.4]" style={{ color: USA_FLAG_BLUE }}>
                      <Signature className="w-4 h-4 md:w-5 md:h-5 mx-auto" />
                    </div>
                  </div>
                </div>
              </div>
            );
          })}
        </div>

        <footer className="p-6 border-t border-black/10 text-center relative z-10">
          <p className="text-[8px] font-mono text-black/20 uppercase tracking-[0.5em]">
            SYSTEM_ID: {Math.random().toString(36).substring(2, 10).toUpperCase()}
          </p>
        </footer>
      </div>

      <Dialog open={!!selectedStamp} onOpenChange={() => setSelectedStamp(null)}>
        {selectedStamp && (
          <DialogContent className="max-w-[90vw] md:max-w-md p-0 rounded-none border-black border-2 bg-[#fdfcf0] font-serif shadow-[12px_12px_0px_0px_rgba(0,0,0,1)]">
             <DialogHeader className="sr-only">
               <DialogTitle>{selectedStamp.location} Entry</DialogTitle>
               <DialogDescription>Details for the travel stamp from {selectedStamp.location}.</DialogDescription>
             </DialogHeader>
             <div className="relative p-6 md:p-10 space-y-6">
                <div className="absolute inset-0 opacity-[0.05] pointer-events-none bg-[url('https://www.transparenttextures.com/patterns/pinstripe-light.png')]" />
                <div className="flex justify-between items-start relative z-10">
                  <div>
                    <h3 className="text-2xl italic text-black">{selectedStamp.location}</h3>
                    <p className="text-[10px] font-mono uppercase tracking-widest text-black/40">
                      {new Date(selectedStamp.stamp_date).toLocaleDateString(undefined, { month: 'long', day: 'numeric', year: 'numeric' })}
                    </p>
                  </div>
                  <div className="flex gap-2">
                    <Button size="icon" variant="ghost" onClick={() => { setEditingStamp(selectedStamp); setSelectedStamp(null); }}>
                      <Pencil className="w-4 h-4" />
                    </Button>
                    <Button size="icon" variant="ghost" onClick={() => handleDelete(selectedStamp.id)} className="text-red-800/50">
                      <Trash2 className="w-4 h-4" />
                    </Button>
                  </div>
                </div>
                <div className="border-t border-black/10 pt-6 italic text-sm text-black/70 leading-relaxed min-h-[80px]">
                  {selectedStamp.field_notes || "No digital records found."}
                </div>
             </div>
          </DialogContent>
        )}
      </Dialog>

      {editingStamp && (
        <StampForm open={!!editingStamp} onClose={() => setEditingStamp(null)} editStamp={editingStamp} />
      )}
    </div>
  );
}